// Shared layout for every library hub page (category, subcategory, tag, new).
// Fully server-rendered: the gem names, descriptions and links are in the HTML.

import { Link } from "@tanstack/react-router";
import { LibraryCard } from "@/components/library-card";
import { SiteFooter } from "@/components/site-footer";
import { LibraryHelpCta } from "@/components/library-help-cta";
import type { HubGem } from "@/lib/library-hubs";

export type Crumb = { label: string; to?: string };

export function LibraryHubPage({
  eyebrow,
  title,
  intro,
  gems,
  crumbs,
  related,
  relatedTitle,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  gems: HubGem[];
  crumbs: Crumb[];
  related?: { label: string; href: string; count: number }[];
  relatedTitle?: string;
}) {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" className="mx-auto max-w-6xl px-5 py-10 md:px-8 md:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            {crumbs.map((c, i) => (
              <li key={c.label} className="flex items-center gap-2">
                {c.to ? (
                  <a href={c.to} className="transition-colors hover:text-foreground">
                    {c.label}
                  </a>
                ) : (
                  <span className="text-foreground">{c.label}</span>
                )}
                {i < crumbs.length - 1 ? <span aria-hidden>/</span> : null}
              </li>
            ))}
          </ol>
        </nav>

        <p className="mt-8 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </p>
        <h1
          className="mt-4 font-display tracking-[-0.035em] leading-[1.04] max-w-[22ch]"
          style={{ fontSize: "clamp(2rem, 4.6vw, 3.6rem)" }}
        >
          {title}
        </h1>
        <p className="mt-6 max-w-[62ch] text-base md:text-lg leading-relaxed text-muted-foreground text-pretty">
          {intro}
        </p>

        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          <Link
            to="/library"
            className="inline-flex min-h-[40px] items-center rounded-full border border-border px-4 transition-colors hover:bg-muted"
          >
            Search all gems
          </Link>
          <a
            href="/library/new"
            className="inline-flex min-h-[40px] items-center rounded-full border border-border px-4 transition-colors hover:bg-muted"
          >
            Newest additions
          </a>
        </div>

        {related && related.length > 0 ? (
          <section className="mt-12">
            <h2 className="font-display text-xl tracking-tight">
              {relatedTitle ?? "Browse deeper"}
            </h2>
            <ul className="mt-4 flex flex-wrap gap-2">
              {related.map((r) => (
                <li key={r.href}>
                  <a
                    href={r.href}
                    className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-border px-3 text-sm transition-colors hover:bg-muted"
                  >
                    {r.label}
                    <span className="tabular-nums text-muted-foreground">{r.count}</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="mt-12">
          <h2 className="sr-only">Gems in this collection</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {gems.map((g, i) => (
              <LibraryCard
                key={g.slug}
                slug={g.slug}
                name={g.name}
                description={g.description ?? ""}
                category={g.category}
                subcategory={g.subcategory}
                tags={[]}
                author={g.author ?? ""}
                license={g.license ?? ""}
                ogImageUrl={g.og_card_url ?? g.og_image_url ?? ""}
                eager={i < 3}
              />
            ))}
          </div>
        </section>
        <div className="mx-auto w-full max-w-6xl px-5 pb-16 md:px-8">
          <LibraryHelpCta line="We're full-stack designers and a vibe coding agency. These are the tools we reach for — if you'd rather someone designed and shipped the thing with them, that's our job." />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

/** ItemList JSON-LD for a hub listing. */
export function itemListJsonLd(name: string, url: string, gems: HubGem[]) {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url,
    numberOfItems: gems.length,
    itemListElement: gems.slice(0, 100).map((g, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://vibing.inc/library/${g.slug}`,
      name: g.name,
    })),
  });
}

/**
 * Marks a hub as a citable, structured collection rather than a blog post —
 * the shape assistants prefer when deciding what to quote.
 */
export function datasetJsonLd(opts: {
  name: string;
  description: string;
  url: string;
  count: number;
  keywords?: string[];
}) {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    license: "https://vibing.inc/terms",
    isAccessibleForFree: true,
    creator: {
      "@type": "Organization",
      name: "the Vibing Company",
      url: "https://vibing.inc",
    },
    keywords: opts.keywords,
    variableMeasured: [
      { "@type": "PropertyValue", name: "entries", value: opts.count },
    ],
    distribution: [
      {
        "@type": "DataDownload",
        encodingFormat: "application/json",
        contentUrl: "https://vibing.inc/api/public/library/search",
      },
      {
        "@type": "DataDownload",
        encodingFormat: "text/markdown",
        contentUrl: `https://vibing.inc/md${new URL(opts.url).pathname}`,
      },
    ],
  });
}

export function breadcrumbJsonLd(crumbs: { name: string; item: string }[]) {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.item,
    })),
  });
}
